// Do not remove following two lazysizes imports. If this is removed most images will not load.
import "lazysizes";
import "lazysizes/plugins/attrchange/ls.attrchange";
import "../styles.css";
import "../styles/chat.css";
import "../styles/core.css";

import { useEffect } from "react";
import serverConfig from "config/server";
import { getCookie } from "cookies-next";
import App, { AppProps } from "next/app";
import { Public_Sans } from "next/font/google";
import { Provider } from "react-redux";
import addressTokenMiddleware from "server/middleware/addressTokenMiddleware";

import { getUserLocationData } from "api/requests/getUserLocation";
import datadogInit from "lib/datadogInit";
import { setDomainCountryCode } from "redux/action-creators/config";
import { LOAD_REQUEST_CONTEXT } from "redux/reducers/config";
import { LOCATION_GEO_LOAD, LocationLoadAction } from "redux/reducers/location";
import { getDomainCountryCode } from "redux/selectors/config";
import { getHasLoadedLocationCookie } from "redux/selectors/location";
import { wrapper } from "redux/store";

import Layout from "components/Layout";

const publicSansFont = Public_Sans({
  display: "swap",
  subsets: ["latin"],
});

const LeaflyApp = ({
  Component,
  ...rest
}: AppProps<{
  currentPath?: string;
  disableFixedHeader?: boolean;
  disableHeaderCollapseOnScroll?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  isAppBannerCookieDismissed?: boolean;
  showMinimalHeader?: boolean;
  /**
   * Permits passing the Datadog configuration from the server to the client.
   * This will be defined on the first page load, but not on any subsequent
   * client-side navigations.
   */
  datadog: {
    env?: string;
    version?: string;
  };
}>) => {
  const {
    store,
    props: { pageProps },
  } = wrapper.useWrappedStore(rest);

  const {
    currentPath,
    disableFixedHeader,
    disableHeaderCollapseOnScroll,
    hideFooter,
    hideHeader,
    isAppBannerCookieDismissed,
    showMinimalHeader,
    datadog,
    ...remainingPageProps
  } = pageProps;

  useEffect(() => {
    if (datadog.env && datadog.version) {
      datadogInit(datadog);
    }
  }, []);

  return (
    <Provider store={store}>
      <Layout
        currentPath={currentPath}
        disableFixedHeader={disableFixedHeader}
        disableHeaderCollapseOnScroll={disableHeaderCollapseOnScroll}
        hideFooter={hideFooter}
        hideHeader={hideHeader}
        isAppBannerCookieDismissed={isAppBannerCookieDismissed}
        showMinimalHeader={showMinimalHeader}
      >
        <Component {...remainingPageProps} />
      </Layout>
      <style jsx global>{`
        html {
          font-family: ${publicSansFont.style.fontFamily};
        }
      `}</style>
    </Provider>
  );
};

LeaflyApp.getInitialProps = wrapper.getInitialAppProps(
  (store) => async (props) => {
    const { ctx } = props;

    if (ctx.req && ctx.res) {
      if (await addressTokenMiddleware(ctx.req, ctx.res)) {
        /**
         * This should never result in the page actually being rendered, as the
         * above function will issue a redirect when necessary.
         */
        return { pageProps: {} };
      }

      store.dispatch({
        context: ctx,
        type: LOAD_REQUEST_CONTEXT,
      });
      store.dispatch(setDomainCountryCode(ctx));

      const countryCode = getDomainCountryCode(store.getState());
      const hasLoadedLocationFromCookie = getHasLoadedLocationCookie(
        store.getState()
      );

      if (!hasLoadedLocationFromCookie) {
        const userLocationData = await getUserLocationData(countryCode, ctx);
        store.dispatch<LocationLoadAction>({
          location: userLocationData,
          type: LOCATION_GEO_LOAD,
        });
      }
    }

    // If passing either hideFooter or hideHeader as a prop on individual
    // pages, the passed prop will take precedence over these
    const hideHeader = ctx.query["hide_header"] === "true";
    const hideFooter = ctx.query["hide_footer"] === "true";

    const isAppBannerCookieDismissed = Boolean(
      getCookie("appBannerDismissed", ctx)
    );

    return {
      pageProps: {
        currentPath: ctx.asPath,
        datadog:
          typeof window === "undefined"
            ? {
                env: serverConfig.datadogEnvironment,
                version: serverConfig.datadogVersion,
              }
            : {},
        hideFooter,
        hideHeader,
        isAppBannerCookieDismissed: isAppBannerCookieDismissed,
        ...(await App.getInitialProps(props)).pageProps,
      },
    };
  }
);

export default LeaflyApp;
